import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AdminEmailsPanel } from "@/components/AdminEmailsPanel";
import { DigestStrip } from "@/components/DigestStrip";
import { QuoteDetailDialog } from "@/components/QuoteDetailDialog";
import { AdminMembers } from "@/components/AdminMembers";
import { AdminStats } from "@/components/admin/AdminStats";
import { AdminTabs, AdminHeaderActions, TABS } from "@/components/admin/AdminTabs";
import { AdminOrdersTab } from "@/components/admin/AdminOrdersTab";
import { AdminQuotesTab } from "@/components/admin/AdminQuotesTab";
import { AdminStoriesTab } from "@/components/admin/AdminStoriesTab";
import { AdminPreviewsTab } from "@/components/admin/AdminPreviewsTab";
import { AdminInterestTab } from "@/components/admin/AdminInterestTab";
import { AdminPricingTab } from "@/components/admin/AdminPricingTab";
import { AdminContentTab } from "@/components/admin/AdminContentTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminForbidden = () => (
  <main className="min-h-screen flex items-center justify-center px-6" data-testid="admin-forbidden">
    <div className="text-center">
      <div className="section-tag mb-4">403</div>
      <h1 className="font-display text-5xl tracking-tight">Admins only.</h1>
      <p className="text-[#78716C] mt-3">Taking you back to your dashboard…</p>
    </div>
  </main>
);

export default function AdminReport() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(TABS.includes(params.get("tab")) ? params.get("tab") : "orders");
  const [openQuote, setOpenQuote] = useState(null);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rep, sto] = await Promise.all([
        axios.get(`${API}/admin/report`),
        axios.get(`${API}/admin/stories`).catch(() => ({ data: { stories: [] } })),
      ]);
      setData(rep.data);
      setStories(sto.data?.stories || []);
    } catch {
      setData({ error: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  useEffect(() => {
    const qid = params.get("quote");
    const q = qid && data?.quotes?.find((x) => x.id === qid);
    if (q) setOpenQuote(q);
  }, [data, params]);

  useEffect(() => {
    if (user && !isAdmin) {
      toast.message("Admins only — sent you back to your dashboard.");
      navigate("/dashboard", { replace: true });
    }
  }, [user, isAdmin, navigate]);

  if (user && !isAdmin) return <AdminForbidden />;

  const s = data?.stats || {};
  const openQuoteById = (id) => {
    const q = (data?.quotes || []).find((x) => x.id === id);
    if (q) setOpenQuote(q);
  };

  return (
    <main className="min-h-screen pt-32 md:pt-40 pb-28" data-testid="admin-report">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="section-tag mb-4">§ Admin · Report</div>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.9] tracking-tight">
              The <span className="italic text-[#B45309]">numbers</span>.
            </h1>
          </div>
          <AdminHeaderActions loading={loading} onRefresh={load} />
        </div>

        {loading && !data && <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#78716C] animate-pulse">Loading report…</p>}

        {data && !data.error && (
          <>
            <DigestStrip onOpenQuote={openQuoteById} />
            <QuoteDetailDialog quote={openQuote} onClose={() => setOpenQuote(null)} onChanged={load} />
            <AdminStats stats={s} />
            <AdminTabs tab={tab} onChange={setTab} pendingStories={s.pending_stories} />

            {tab === "orders" && <AdminOrdersTab orders={data.orders || []} stats={s} onReload={load} />}
            {tab === "quotes" && <AdminQuotesTab quotes={data.quotes || []} onReload={load} onOpen={setOpenQuote} />}
            {tab === "pricing" && <AdminPricingTab />}
            {tab === "stories" && <AdminStoriesTab stories={stories} setStories={setStories} />}
            {tab === "previews" && <AdminPreviewsTab topPreviews={data.top_previews || []} />}
            {tab === "interest" && <AdminInterestTab />}
            {tab === "members" && <AdminMembers onChanged={load} />}
            {tab === "emails" && <AdminEmailsPanel />}
            {tab === "content" && <AdminContentTab />}
          </>
        )}
      </div>
    </main>
  );
}
